import { Contactpersoon, useGetContactpersonen } from "@/entities/contactpersoon";
import { Button, FormField, FormItem, FormMessage, MultiSelect, Skeleton } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { useMemo, useState } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { NieuwContactpersoon } from "./nieuw-contactpersoon-dialog";

type ContactpersonenMultiSelectProps<T extends FieldValues> = {
    form: UseFormReturn<T>;
    name: FieldPath<T>;
    dossierId: string;
    onCreate: (contact: Contactpersoon) => void;
    allowCreateContactpersoon?: boolean;
};

export function ContactpersonenMultiSelect<T extends FieldValues>({
    form,
    name,
    dossierId,
    onCreate,
    allowCreateContactpersoon = true,
}: Readonly<ContactpersonenMultiSelectProps<T>>) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const { data: contactpersonen, isPending, error } = useGetContactpersonen(dossierId);
    const options = useMemo(() => getContactpersonenOptions(contactpersonen ?? []), [contactpersonen]);

    if (isPending) {
        return <Skeleton className="h-10 w-full" />;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van contactpersonen" />;
    }

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem className="grid grid-cols-[1fr_auto] gap-2">
                    <MultiSelect
                        onValueChange={field.onChange}
                        value={field.value}
                        options={options}
                        search={{ emptyMessage: "Geen medewerkers gevonden", placeholder: "Selecteer medewerker..." }}
                        placeholder="Selecteer ontvanger(s)"
                        aria-label="Medewerkers"
                    />
                    {allowCreateContactpersoon && (
                        <>
                            <Button
                                type="button"
                                variant="tertiary"
                                icon="user-plus"
                                iconStrokeWidth="thick"
                                onClick={() => setDialogOpen(true)}
                            >
                                Nieuwe medewerker
                            </Button>
                            <NieuwContactpersoon
                                dossierId={dossierId}
                                onCreate={onCreate}
                                open={dialogOpen}
                                onOpenChange={setDialogOpen}
                            ></NieuwContactpersoon>
                        </>
                    )}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}

const getContactpersonenOptions = (stations: Contactpersoon[]) =>
    stations.map((contact) => ({ label: Contactpersoon.getDisplayName(contact), value: contact.id.toString() }));
